import React from "react";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import { Link, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { FiLock, FiUser, FiUsers } from "react-icons/fi";
import { selectCurrentCompanyId } from "../../../store/auth/index.js";
import { withInvestorQuery } from "../../../utils/investorRouteHelpers";
import { extractCapabilitiesFromCoopData } from "../../../utils/investorCapabilities";
import { CAPABILITY_KEYS } from "../../../hooks/useCompanyCapabilities";
import {
  AuthShell,
  Button,
  PasswordField,
  TextField,
  FocusFirstError,
} from "../../Auth";

const LOGIN_POINTS = [
  "Your contributions, loans and records in one place.",
  "Members and staff use the same sign in.",
];

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Accepts an email, or a phone in any of: +2348012345678, 2348012345678, 08012345678, 8012345678
const validationSchema = Yup.object({
  identifier: Yup.string()
    .trim()
    .required("Enter your email or phone number")
    .test("is-email-or-phone", "Enter a valid email or phone number", (value) => {
      if (!value) return false;
      if (EMAIL_RE.test(value)) return true;
      const cleaned = value.replace(/[\s\-()]/g, "");
      return [/^\+?\d{10,15}$/, /^0\d{9,10}$/, /^\d{10,11}$/].some((re) => re.test(cleaned));
    }),
  password: Yup.string().required("Enter your password"),
});

export default function LoginPage({
  handleLoginSubmit,
  companyDetails,
  isLoading,
  isCompanyLoading = false,
  preservedQuery = {},
}) {
  const navigate = useNavigate();
  const coopId = useSelector(selectCurrentCompanyId);
  const companyId = companyDetails?.company_id || coopId;
  const investorSignupPath = withInvestorQuery("/invest/signup", preservedQuery);

  const caps = extractCapabilitiesFromCoopData(companyDetails);
  // Only show when the API explicitly enables both the hub and public signup.
  const showInvestorSignup = Boolean(
    caps?.[CAPABILITY_KEYS.INVESTOR_PORTAL] === true && caps?.investorSignupEnabled === true
  );

  return (
    <AuthShell company={companyDetails} loading={isCompanyLoading} points={LOGIN_POINTS}>
      <div className="ax-card">
        <div>
          <h1 className="ax-h1">Sign in</h1>
          <p className="ax-sub">
            {showInvestorSignup
              ? "Members, investors and staff all sign in here. We take you to the right dashboard."
              : "Members and staff sign in here. We take you to the right dashboard."}
          </p>
        </div>

        <Formik
          initialValues={{ identifier: "", password: "", company_id: companyId }}
          validationSchema={validationSchema}
          onSubmit={handleLoginSubmit}
          enableReinitialize
        >
          {({ isSubmitting }) => (
            <Form
              noValidate
              style={{ display: "flex", flexDirection: "column", gap: 16 }}
            >
              <FocusFirstError />
              <TextField
                name="identifier"
                label="Email or phone number"
                icon={FiUser}
                autoComplete="username"
                autoCapitalize="none"
                spellCheck={false}
                inputMode="email"
                placeholder="Email or phone number"
                hint="Use your email, or a phone number like 08012345678."
              />
              <PasswordField
                name="password"
                label="Password"
                icon={FiLock}
                autoComplete="current-password"
                placeholder="Your password"
                labelAside={
                  <Link className="ax-link" to="/forget_password">
                    Forgot password?
                  </Link>
                }
              />
              <Button type="submit" loading={isLoading || isSubmitting}>
                Sign in
              </Button>
            </Form>
          )}
        </Formik>

        <div className="ax-divider">New to {companyDetails?.name ? "this cooperative" : "us"}?</div>
        <Button variant="outline" onClick={() => navigate("/member-signup")}>
          <FiUsers aria-hidden="true" size={18} />
          Become a member
        </Button>
        {showInvestorSignup ? (
          <p className="ax-center ax-hint">
            Investing instead?{" "}
            <Link to={investorSignupPath} className="ax-link">
              Create an investor account
            </Link>
          </p>
        ) : null}
      </div>
    </AuthShell>
  );
}
